import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FreeTrialService } from '../../services/free-trial.service';
import { FreeTrial } from '@app/Models/free-trial.model';
import { Tenant } from '@app/Models/tenant';
import { TenantService } from '@app/Services';
import { BehaviorSubject, Subject } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TrialStatus } from '@app/Enums';
import { filter, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { DialogResult, PopupDialog } from '@app/Common';
import { ActivatedRoute, Router } from '@angular/router';
import { ExtendTrialPopupComponent, ExtendTrialResult } from '../extend-trial-expiy-date-popup';
import { AsyncPipe } from '@angular/common';
import { MatCard, MatCardTitle } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatFormField, MatLabel, MatInput, MatSuffix } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';

@Component({
    selector: 'app-trial-page-panel',
    templateUrl: './trial-config-page.component.html',
    styleUrls: ['./trial-config-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatCard, MatCardTitle, MatButton, MatDivider, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, AsyncPipe]
})
export class TrialConfigPageComponent implements OnInit, OnDestroy {
  freeTrialProperties$: BehaviorSubject<FreeTrial> = new BehaviorSubject<FreeTrial | null>(null);
  freeTrialForm = new UntypedFormGroup({
    trialType: new UntypedFormControl({ value: '', disabled: true }),
    trialStartDate: new UntypedFormControl({ value: '', disabled: true }),
    trialEndDate: new UntypedFormControl({ value: '', disabled: false }),
    trialPromotedDate: new UntypedFormControl({ value: '', disabled: true }),
  });
  private isDestroyed$ = new Subject<void>();

  constructor(
    private tenantService: TenantService,
    private freeTrialService: FreeTrialService,
    private dialog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.fetchFreeTrial();
    this.subscribeToFormUpdates();
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  get selectedTenant(): Tenant {
    return this.tenantService.selectedTenant;
  }

  getRemainingDays(): number {
    const startDate = this.removeTimzezoneFromDate(new Date());
    startDate.setHours(0,0,0,0);
    const endDate = new Date(this.freeTrialForm.get('trialEndDate')?.value ?? null);

    const diffInMiliseconds = endDate.getTime() - startDate.getTime();
    const days = Math.round(diffInMiliseconds / (1000 * 60 * 60 * 24));
    return days < 0 ? 0 : days;
  }

  getMaxExpiryDate(startDateString: string): Date {
    const startDate = new Date(startDateString ?? null);
    const maximumDate = Math.round(startDate.getTime() + (1000 * 60 * 60 * 24 * 180));
    return new Date(maximumDate);
  }

  getTrialStatusClass(freeTrialProperties: FreeTrial): string {
    if (!freeTrialProperties || !freeTrialProperties.TrialStatus) {
      return '';
    }

    return freeTrialProperties.TrialStatus.toLowerCase();
  }

  showCancelConfirmationPopup(): void {
    const dialogRef =
      this.dialog.open<PopupDialog, any, DialogResult>(PopupDialog);
    dialogRef.componentInstance.DialogTitle = 'Cancel Trial';
    dialogRef.componentInstance.DialogDescription = 'Are you sure you want to cancel the trial for tenant ' +
      `<b>${this.tenantService.selectedTenant?.Value}</b>?`;
    dialogRef.componentInstance.DisplayTextArea = true;
    dialogRef.componentInstance.DisplayTextAreaLength = 511;
    dialogRef.componentInstance.DialogPlaceholderTextArea = 'Reason of cancellation (Optional)';
    dialogRef.componentInstance.DialogConfirmButtonText = 'Confirm';
    dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined && result.Result) {
        this.cancel(result.Text);
      }
    });
  }

  showReactivateConfirmationPopup(): void {
    const dialogRef =
      this.dialog.open<PopupDialog, any, DialogResult>(PopupDialog);
    dialogRef.componentInstance.DialogTitle = 'Reactivate Trial';
    dialogRef.componentInstance.DialogDescription = 'Are you sure you want to reactivate the cancelled trial for tenant ' +
      `<b>${this.tenantService.selectedTenant?.Value}</b>?`;
    dialogRef.componentInstance.DisplayTextArea = true;
    dialogRef.componentInstance.DisplayTextAreaLength = 511;
    dialogRef.componentInstance.DialogPlaceholderTextArea = 'Reason of reactivation (Optional)';
    dialogRef.componentInstance.DialogConfirmButtonText = 'Confirm';
    dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined && result.Result) {
        this.reactivateTrial(result.Text);
      }
    });
  }

  showUpgradeConfirmationPopup(): void {
    const dialogRef =
      this.dialog.open<PopupDialog, any, DialogResult>(PopupDialog);
    dialogRef.componentInstance.DialogTitle = 'Upgrade to Paid Tenant';
    dialogRef.componentInstance.DialogDescription = 'Are you sure you want to upgrade the tenant ' +
      `<b>${this.tenantService.selectedTenant?.Value}</b>\n` +
      'from a Trial to a Paid Tenant?';
    dialogRef.componentInstance.DialogConfirmButtonText = 'Upgrade';
    dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined && result.Result) {
        this.upgradeToPaidTenant();
      }
    });
  }

  showExtendConfirmationPopup(): void {
    const dialogRef =
      this.dialog.open<ExtendTrialPopupComponent, FreeTrial, ExtendTrialResult>(
        ExtendTrialPopupComponent, { data: this.freeTrialProperties$.value }
      );

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.isSave) {
        const expiryDate = new Date(result.payload);
        const newExpiryDateNoTimezone = this.removeTimzezoneFromDate(expiryDate);
        this.extendTrial(newExpiryDateNoTimezone);
      }
    });
  }

  saveTrialChanges(): void {
    const newExpiryDate = new Date(this.freeTrialForm.get('trialEndDate')?.value);
    const newExpiryDateNoTimezone = this.removeTimzezoneFromDate(newExpiryDate);

    const newTrial: FreeTrial = {
      ...this.freeTrialProperties$.value,
      TrialExpiryDate: newExpiryDateNoTimezone.toJSON()
    };
    this.updateTrial(newTrial);
  }

  isCancelButtonAvailable(freeTrialProperties: FreeTrial): boolean {
    if (!freeTrialProperties || !freeTrialProperties.TrialStatus) {
      return false;
    }

    return freeTrialProperties.TrialStatus === TrialStatus.Active;
  }

  isUpgradeButtonAvailable(freeTrialProperties: FreeTrial): boolean {
    if (!freeTrialProperties || !freeTrialProperties.TrialStatus) {
      return false;
    }

    return freeTrialProperties.TrialStatus !== TrialStatus.Promoted;
  }

  isExtendButtonAvailable(freeTrialProperties: FreeTrial): boolean {
    if (!freeTrialProperties || !freeTrialProperties.TrialStatus) {
      return false;
    }

    return freeTrialProperties.TrialStatus === TrialStatus.Expired;
  }

  isSaveChangesButtonAvailable(freeTrialProperties: FreeTrial): boolean {
    return freeTrialProperties.TrialStatus !== TrialStatus.Promoted;
  }

  isTrialPromoted(freeTrialProperties: FreeTrial): boolean {
    return freeTrialProperties.TrialStatus === TrialStatus.Promoted;
  }

  isReactivateButtonAvailable(freeTrialProperties: FreeTrial): boolean {
    return freeTrialProperties.TrialStatus === TrialStatus.Cancelled;
  }

  private fetchFreeTrial(): void {
    this.freeTrialService
      .getFreeTrial(this.selectedTenant.TenantId)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((properties) => (this.freeTrialProperties$.next(properties)));
  }

  private subscribeToFormUpdates(): void {
    this.freeTrialProperties$
      .pipe(
        filter(properties => properties !== null),
        takeUntil(this.isDestroyed$)
      )
      .subscribe(properties => {
        this.freeTrialForm.get('trialType')?.setValue(properties.TrialTypeIndustry);
        this.freeTrialForm.get('trialStartDate')?.setValue(properties.TrialStartDate);
        this.freeTrialForm.get('trialEndDate')?.setValue(properties.TrialExpiryDate);
        this.freeTrialForm.get('trialPromotedDate')?.setValue(properties.TrialPromotedDate);

        if (properties.TrialStatus === TrialStatus.Active) {
          this.freeTrialForm.get('trialEndDate')?.enable();
        } else {
          this.freeTrialForm.get('trialEndDate')?.disable();
        }
      });
  }

  private cancel(reason: string): void {
    this.freeTrialService.cancelTrial(this.selectedTenant.TenantId, reason)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((properties) => (this.freeTrialProperties$.next(properties)));
  }

  private reactivateTrial(reason: string): void {
    this.freeTrialService.reactivateTrial(this.selectedTenant.TenantId, reason)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((properties) => (this.freeTrialProperties$.next(properties)));
  }

  private upgradeToPaidTenant(): void {
    this.freeTrialService.upgradeToPaidTenant(this.selectedTenant.TenantId)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(() => {
        this.router.navigate(['../../overview'], { relativeTo: this.activatedRoute });
      });
  }

  private extendTrial(newExpiryDate: Date): void {
    this.freeTrialService.extendTrial(this.selectedTenant.TenantId, newExpiryDate)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((properties) => (this.freeTrialProperties$.next(properties)));
  }

  private updateTrial(newTrial: FreeTrial): void {
    this.freeTrialService.updateTrial(this.selectedTenant.TenantId, newTrial)
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((properties) => {
        this.freeTrialForm.reset();
        this.freeTrialProperties$.next(properties);
      });
  }

  private removeTimzezoneFromDate(date: Date): Date {
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - userTimezoneOffset);
  }
}
